import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Megaphone, X } from "lucide-react";

const DISMISSED_KEY = "announcement_dismissed_id";

interface AnnouncementPopupProps {
  externalOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onShowNewsButton?: (show: boolean) => void;
}

const AnnouncementPopup = ({ externalOpen, onOpenChange, onShowNewsButton }: AnnouncementPopupProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen ?? internalOpen;

  const { data: announcement } = useQuery({
    queryKey: ["active-announcement"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_announcements")
        .select("*")
        .eq("is_active", true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (announcement) {
      const dismissed = sessionStorage.getItem(DISMISSED_KEY);
      if (dismissed !== announcement.id) {
        setInternalOpen(true);
        onOpenChange?.(true);
      } else {
        onShowNewsButton?.(true);
      }
    }
  }, [announcement]);

  const handleClose = () => {
    setInternalOpen(false);
    onOpenChange?.(false);
    onShowNewsButton?.(true);
    if (announcement) {
      sessionStorage.setItem(DISMISSED_KEY, announcement.id);
    }
  };

  if (!announcement || !open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative z-10 w-full max-w-lg bg-card border border-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-primary/10 px-6 py-3.5 flex items-center justify-between border-b border-border/50">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-primary" />
            <span
              className="text-sm tracking-[0.15em] uppercase font-semibold text-primary"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Aktuelles
            </span>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
            aria-label="Schließen"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5">
          <h3 className="text-xl font-semibold text-primary mb-3">
            {announcement.title}
          </h3>
          {announcement.message && (
            <p className="text-base text-foreground/80 leading-relaxed whitespace-pre-line">
              {announcement.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup;
